import React, { useEffect, useState } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { DEMO_CAMPAIGNS } from '../../constants/demo-copy';
import {
  AnimatedNumber,
  usePrefersReducedMotion
} from '../motion';

const Panel = styled('div')`
  position: relative;
  width: 100%;
  max-width: 560px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 16px 48px rgba(14, 28, 43, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
`;

const PanelHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
`;

const MonoLabel = styled('span')`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
`;

const Dots = styled('div')`
  display: flex;
  gap: 6px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #e2e8f0;
  }
`;

const fadeInCard = `
  @keyframes montageCardIn {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
`;

const Card = styled('div')<{ visible: boolean; reduced: boolean }>`
  ${fadeInCard}

  position: relative;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  animation: ${(p) =>
    p.visible && !p.reduced ? 'montageCardIn 0.6s ease both' : 'none'};
`;

const ChatBubbleUser = styled('div')`
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 12px 12px 4px 12px;
  color: #0e1c2b;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  align-self: flex-end;
  max-width: 85%;
  min-height: 45px;
  position: relative;
`;

const AgentReply = styled('div')<{ visible: boolean; reduced: boolean }>`
  ${fadeInCard}

  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #4f4f4f;
  font-size: 14px;
  line-height: 1.5;
  max-width: 92%;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  animation: ${(p) =>
    p.visible && !p.reduced ? 'montageCardIn 0.5s ease both' : 'none'};
`;

const AgentAvatar = styled('div')`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background-color: #2e5cff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardTitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CardTitle = styled('div')`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
`;

const StatsRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const BigNumber = styled('div')`
  font-size: 24px;
  font-weight: 800;
  color: #0e1c2b;
  line-height: 1.1;
  margin-bottom: 4px;
`;

const StatCaption = styled('div')`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const SwapRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #4f4f4f;
  padding: 6px 0;
  border-top: 1px solid #f1f5f9;

  strong {
    color: #0e1c2b;
  }
`;

const SwapValue = styled('span')`
  font-weight: 700;
  color: #237804;
  background-color: #f0fdf4;
  border-radius: 4px;
  padding: 2px 6px;
`;

const USER_PROMPT =
  'Which sponsorships are behind pace before Friday\u2019s client check-in?';

export const AgenticMontage = () => {
  const reduced = usePrefersReducedMotion();
  // 0 = user prompt, 1 = agent reply, 2 = stat cards
  const [step, setStep] = useState(reduced ? 2 : 0);

  useEffect(() => {
    if (reduced) {
      setStep(2);
      return;
    }
    const timers = [
      window.setTimeout(() => setStep((s) => Math.max(s, 1)), 400),
      window.setTimeout(() => setStep((s) => Math.max(s, 2)), 1150)
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [reduced]);

  return (
    <Panel aria-label="Product preview: campaign delivery and yield opportunities">
      <PanelHeader>
        <MonoLabel>Agent</MonoLabel>
        <Dots>
          <span />
          <span />
          <span />
        </Dots>
      </PanelHeader>

      <ChatBubbleUser>{USER_PROMPT}</ChatBubbleUser>

      <AgentReply visible={step >= 1} reduced={reduced}>
        <AgentAvatar>
          <Sparkles size={14} />
        </AgentAvatar>
        <div>
          Two deals need attention before Friday. {DEMO_CAMPAIGNS.homeRefresh} is at
          38% of goal with 6 days left; {DEMO_CAMPAIGNS.springAudio} is pacing at 61%.
        </div>
      </AgentReply>

      <StatsRow>
        <Card visible={step >= 2} reduced={reduced}>
          <CardTitleRow>
            <CardTitle>Campaign delivery</CardTitle>
          </CardTitleRow>
          <BigNumber>14 in flight</BigNumber>
          <StatCaption>2 behind pace · 6 brands</StatCaption>
          <div style={{ marginTop: 10 }}>
            <SwapRow>
              <span>
                <strong>{DEMO_CAMPAIGNS.homeRefresh}</strong>
              </span>
              <SwapValue>38% of goal</SwapValue>
            </SwapRow>
            <SwapRow>
              <span>
                <strong>{DEMO_CAMPAIGNS.springAudio}</strong>
              </span>
              <SwapValue>61% of goal</SwapValue>
            </SwapRow>
          </div>
        </Card>

        <Card visible={step >= 2} reduced={reduced}>
          <CardTitleRow>
            <CardTitle>Yield optimization</CardTitle>
            <Zap size={14} color="#2e5cff" />
          </CardTitleRow>
          <BigNumber>
            +$
            <AnimatedNumber
              value={77.9}
              start={step >= 2}
              format={(n) => n.toFixed(1)}
            />
            K
          </BigNumber>
          <StatCaption>Monthly opportunity volume</StatCaption>
          <div style={{ marginTop: 10 }}>
            <SwapRow>
              <span>
                23K clicks → <strong>Best Buy</strong>
              </span>
              <SwapValue>+$14.5K</SwapValue>
            </SwapRow>
            <SwapRow>
              <span>
                17K clicks → <strong>Samsung</strong>
              </span>
              <SwapValue>+$8.2K</SwapValue>
            </SwapRow>
          </div>
        </Card>
      </StatsRow>
    </Panel>
  );
};
